import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getDefaultMenuContext,
  getSelectedMenuContext,
  normalizeMenuContexts,
  buildMenuTree,
  normalizeMenuItems,
  upsertSelectedMenuContext,
} from "../../../utils/menu";
import type { MenuContextNode, MenuNode } from "../../../utils/menu";

const HIDDEN_MENU_PATHS = new Set(["/dashboard/vendor-invite", "/suit-shop/contracts", "/dress-shop/contracts", "/photo-studio/contracts"]);
const BYPASS_REDIRECT_PREFIXES = ["/suit-shop", "/dress-shop", "/photo-studio", "/makeup-shop", "/wedding-hall"];
const USER_INFO_UPDATED_EVENT = "wedmin:user-info-updated";
const MENU_PATH_ALIASES: Record<string, string[]> = {
  "/dashboard/customers": ["/dashboard/customer-detail", "/dashboard/customer-detail-view"],
  "/customers": ["/dashboard/customer-detail", "/dashboard/customer-detail-view"],
  "/dashboard/customer-detail": ["/dashboard/customers", "/dashboard/customer-detail-view"],
  "/customer-detail": ["/dashboard/customers", "/dashboard/customer-detail-view"],
  "/dashboard/customer-detail-view": ["/dashboard/customers", "/dashboard/customer-detail"],
  "/customer-detail-view": ["/dashboard/customers", "/dashboard/customer-detail"],
  "/dashboard/vendors": ["/dashboard/vendor-detail"],
  "/vendors": ["/dashboard/vendor-detail"],
  "/dashboard/studio-customers": ["/dashboard/studio-customer-detail"],
  "/studio-customers": ["/dashboard/studio-customer-detail"],
  "/dashboard/dress-list": ["/dashboard/dress-detail"],
  "/dress-list": ["/dashboard/dress-detail"],
  "/suit-shop/customers": ["/suit-shop/customer-detail"],
  "/suit-shop/contracts": ["/suit-shop/contract-detail"],
  "/dress-shop/customers": ["/dress-shop/customer-detail"],
  "/dress-shop/contracts": ["/dress-shop/contract-detail"],
  "/photo-studio/customers": ["/photo-studio/customer-detail"],
  "/photo-studio/contracts": ["/photo-studio/contract-detail"],
  "/makeup-shop/customers": ["/makeup-shop/customer-detail"],
  "/wedding-hall/customers": ["/wedding-hall/customer-detail"],
};

const normalizePathname = (path: string) => {
  if (!path) return path;
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
};

const isPathActive = (currentPath: string, menuPath?: string) => {
  if (!menuPath) return false;
  const normalizedCurrentPath = normalizePathname(currentPath);
  const normalizedMenuPath = normalizePathname(menuPath);

  if (normalizedMenuPath === "/dashboard") return normalizedCurrentPath === "/dashboard";
  return (
    normalizedCurrentPath === normalizedMenuPath ||
    normalizedCurrentPath.startsWith(`${normalizedMenuPath}/`)
  );
};

const isPathAllowedByMenu = (currentPath: string, menuPath?: string) => {
  if (!menuPath) return false;
  if (isPathActive(currentPath, menuPath)) return true;

  const aliases = MENU_PATH_ALIASES[menuPath] ?? [];
  return aliases.some((aliasPath) => isPathActive(currentPath, aliasPath));
};

const getFirstAccessiblePath = (menus: MenuNode[]): string | null => {
  const first = menus.find((menu) => typeof menu.path === "string" && menu.path.trim().startsWith("/"));
  return first?.path ?? null;
};

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuTree, setMenuTree] = useState<MenuNode[]>([]);
  const [menuContexts, setMenuContexts] = useState<MenuContextNode[]>([]);
  const [selectedContextKey, setSelectedContextKey] = useState("");
  const [userName, setUserName] = useState("");
  const [contextTab, setContextTab] = useState<"OWNER" | "PARTNER">("OWNER");
  const [contextSearch, setContextSearch] = useState("");
  const [isContextHelpOpen, setIsContextHelpOpen] = useState(false);

  useEffect(() => {
    const applyMenus = (source: any[]) => {
      const normalized = normalizeMenuItems(source).filter(
        (menu) => !menu.path || !HIDDEN_MENU_PATHS.has(menu.path)
      );
      setMenuTree(buildMenuTree(normalized));
      return normalized;
    };

    const persistSelectedContext = (userInfo: any, selectedContext: MenuContextNode) => {
      const updatedUserInfo = upsertSelectedMenuContext(userInfo, selectedContext);
      localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
      window.dispatchEvent(new Event(USER_INFO_UPDATED_EVENT));
    };

    const initialize = () => {
      const userInfoStr = localStorage.getItem("userInfo");
      if (!userInfoStr) return;

      try {
        const userInfo = JSON.parse(userInfoStr);
        setUserName(userInfo.name || "");

        const contexts = normalizeMenuContexts(userInfo);
        setMenuContexts(contexts);

        const selectedContext = getSelectedMenuContext(userInfo) ?? getDefaultMenuContext(contexts);
        if (!selectedContext) {
          setSelectedContextKey("");
          setMenuTree([]);
          return;
        }

        setSelectedContextKey(selectedContext.contextKey);
        const normalizedMenus = applyMenus(selectedContext.menus);
        if (userInfo.selectedMenuContextKey !== selectedContext.contextKey) {
          persistSelectedContext(userInfo, selectedContext);
        }

        if (normalizedMenus.length === 0) {
          setMenuTree([]);
          return;
        }

        const isBypassPath = BYPASS_REDIRECT_PREFIXES.some((prefix) =>
          location.pathname.startsWith(prefix)
        );
        if (!isBypassPath) {
          const isCurrentPathAllowed = normalizedMenus.some((menu) =>
            isPathAllowedByMenu(location.pathname, menu.path)
          );
          if (!isCurrentPathAllowed) {
            const nextPath = getFirstAccessiblePath(normalizedMenus);
            if (nextPath && nextPath !== location.pathname) {
              navigate(nextPath);
            }
          }
        }
      } catch (error) {
        console.error("사이드바 초기화 실패", error);
      }
    };

    initialize();
    const handleExternalUpdate = () => initialize();
    window.addEventListener(USER_INFO_UPDATED_EVENT, handleExternalUpdate);

    return () => {
      window.removeEventListener(USER_INFO_UPDATED_EVENT, handleExternalUpdate);
    };
  }, [location.pathname, navigate]);

  const handleContextChange = (nextContextKey: string) => {
    const nextContext = menuContexts.find((context) => context.contextKey === nextContextKey);
    if (!nextContext) return;

    setSelectedContextKey(nextContext.contextKey);
    setContextSearch("");
    const normalizedMenus = normalizeMenuItems(nextContext.menus).filter(
      (menu) => !menu.path || !HIDDEN_MENU_PATHS.has(menu.path)
    );
    setMenuTree(buildMenuTree(normalizedMenus));

    const userInfoStr = localStorage.getItem("userInfo");
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        const updatedUserInfo = upsertSelectedMenuContext(userInfo, nextContext);
        localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
        window.dispatchEvent(new Event(USER_INFO_UPDATED_EVENT));
      } catch (error) {
        console.error("컨텍스트 저장 실패", error);
      }
    }

    const isBypassPath = BYPASS_REDIRECT_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (!isBypassPath) {
      const isCurrentPathAllowed = normalizedMenus.some((menu) =>
        isPathAllowedByMenu(location.pathname, menu.path)
      );
      if (!isCurrentPathAllowed) {
        const nextPath = getFirstAccessiblePath(normalizedMenus) ?? "/dashboard";
        if (nextPath !== location.pathname) {
          navigate(nextPath);
        }
      }
    }
  };

  const isMenuActive = (menu: MenuNode): boolean => {
    if (isPathAllowedByMenu(location.pathname, menu.path)) return true;
    return Boolean(menu.children?.some((child) => isMenuActive(child)));
  };

  const selectedContext = menuContexts.find((context) => context.contextKey === selectedContextKey) ?? null;
  const sortedContexts = useMemo(
    () =>
      [...menuContexts].sort((a, b) => {
        const nameDiff = a.hostVendorName.localeCompare(b.hostVendorName, "ko");
        if (nameDiff !== 0) return nameDiff;
        return a.contextKey.localeCompare(b.contextKey);
      }),
    [menuContexts]
  );
  const ownerContexts = useMemo(
    () => sortedContexts.filter((context) => context.contextType === "OWNER"),
    [sortedContexts]
  );
  const partnerContexts = useMemo(
    () => sortedContexts.filter((context) => context.contextType === "PARTNER"),
    [sortedContexts]
  );
  const normalizedSearch = contextSearch.trim().toLowerCase();
  const contextsByTab = contextTab === "PARTNER" ? partnerContexts : ownerContexts;
  const filteredContexts = useMemo(() => {
    if (!normalizedSearch) return contextsByTab;
    return contextsByTab.filter((context) =>
      context.hostVendorName.toLowerCase().includes(normalizedSearch)
    );
  }, [contextsByTab, normalizedSearch]);
  const emptyMenuMessage =
    "선택한 업체 기준으로 표시할 메뉴가 없습니다. 연결 상태 또는 권한 설정을 확인해주세요.";

  useEffect(() => {
    setContextTab(selectedContext?.contextType === "PARTNER" ? "PARTNER" : "OWNER");
  }, [selectedContext?.contextType]);

  const renderMenuItems = (nodes: MenuNode[], depth = 0) => {
    return nodes.map((menu) => {
      const isActive = isMenuActive(menu);
      const hasChildren = Boolean(menu.children && menu.children.length > 0);
      const itemIndent = depth > 0 ? "ml-4" : "";
      const itemClass = `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${itemIndent} ${
        isActive ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
      }`;
      const iconClass = `${menu.icon || "ri-menu-line"} text-lg ${isActive ? "text-teal-600" : "text-gray-400"}`;
      const key = `${menu.menuIdx}:${menu.path || menu.name}`;

      return (
        <li key={key}>
          {menu.path ? (
            <Link to={menu.path} className={itemClass}>
              <i className={iconClass}></i>
              <span>{menu.name}</span>
            </Link>
          ) : (
            <div className={itemClass}>
              <i className={iconClass}></i>
              <span>{menu.name}</span>
            </div>
          )}

          {hasChildren && <ul className="mt-1 space-y-1">{renderMenuItems(menu.children || [], depth + 1)}</ul>}
        </li>
      );
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("isMasterAdmin");
    navigate("/login");
  };

  return (
    <aside className="w-64 min-w-64 shrink-0 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0 overflow-y-auto">
      <div className="h-16 flex items-center px-6 border-b border-gray-200">
        <h1 className="text-xl font-bold text-teal-600">WEDMIN</h1>
      </div>

      <div className="p-4 border-b border-gray-100 bg-gray-50 space-y-3">
        <p className="text-sm text-gray-600">안녕하세요,</p>
        <p className="font-semibold text-gray-900">{userName}님</p>

        {sortedContexts.length > 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500">업체 전환</p>
              <button
                type="button"
                onClick={() => setIsContextHelpOpen(true)}
                className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white text-[11px] font-bold text-gray-500 hover:bg-gray-50"
                aria-label="메뉴 전환 안내 보기"
              >
                !
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setContextTab("OWNER");
                  setContextSearch("");
                }}
                className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                  contextTab === "OWNER"
                    ? "bg-teal-50 text-teal-700"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                내 업체({ownerContexts.length})
              </button>
              <button
                type="button"
                onClick={() => {
                  setContextTab("PARTNER");
                  setContextSearch("");
                }}
                disabled={partnerContexts.length === 0}
                className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                  contextTab === "PARTNER"
                    ? "bg-teal-50 text-teal-700"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                } ${partnerContexts.length === 0 ? "cursor-not-allowed opacity-50" : ""}`}
              >
                파트너 협업 ({partnerContexts.length})
              </button>
            </div>

            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={contextSearch}
                onChange={(e) => setContextSearch(e.target.value)}
                placeholder="메뉴 전환 업체 검색"
                className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="max-h-56 overflow-y-auto space-y-1">
              {filteredContexts.length > 0 ? (
                filteredContexts.map((context) => {
                  const isSelected = context.contextKey === selectedContextKey;
                  return (
                    <button
                      key={context.contextKey}
                      type="button"
                      onClick={() => handleContextChange(context.contextKey)}
                      className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${
                        isSelected ? "bg-teal-50 text-teal-700" : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{context.hostVendorName}</p>
                          <p className="text-[11px] text-gray-500">이용 가능한 메뉴 {context.menus.length}개</p>
                        </div>
                        {isSelected && <i className="ri-check-line text-base"></i>}
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="px-3 py-2 text-sm text-gray-500">
                  {contextsByTab.length === 0
                    ? contextTab === "OWNER"
                      ? "표시할 내 메뉴 기준 업체가 없습니다."
                      : "등록된 협업 메뉴 기준 업체가 없습니다."
                    : "검색된 업체가 없습니다."}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {isContextHelpOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
          onClick={() => setIsContextHelpOpen(false)}
        >
          <div
            className="w-full max-w-xs rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-900">메뉴 전환 안내</p>
              <button
                type="button"
                onClick={() => setIsContextHelpOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="안내 닫기"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-xs leading-5 text-gray-600">
              사이드바의 이 영역은 어떤 업체 기준으로 메뉴를 볼지 바꾸는 기능입니다.
              <br />
              업체관리 화면의 거래처 목록과는 별개입니다.
            </p>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {menuTree.length > 0 ? (
            renderMenuItems(menuTree)
          ) : (
            <li className="px-4 py-2 text-sm text-gray-500">
              {emptyMenuMessage}
            </li>
          )}
        </ul>
      </nav>

      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 w-full text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <i className="ri-logout-box-line text-lg"></i>
          <span>로그아웃</span>
        </button>
      </div>
    </aside>
  );
}
